import { normalizeAssistantText } from '@/lib/assistant/assistantTextUtils';
import { visibleMenuProducts } from '@/lib/assistant/assistantProductSearch';

/** Normaliza modismos de pedido antes de matchear contra la carta. */
export const ORDER_QUERY_PHRASE_FIXES = [
  [/\bquier\b/gi, 'quiero'],
  [/\bke\b/gi, 'que'],
  [/\bpza?s?\b/gi, 'piezas'],
  [/\bpieza\b/gi, 'piezas'],
  [/\s+0\s+/g, ' o '],
  [/\b(?:un|una)\s+big\b(?!mati)/gi, 'bigmati'],
  [/\bbig\s+mati\b/gi, 'bigmati'],
  [/\b(?:y)\s+(?:un|una)\s+kids\b/gi, ' y kids'],
  [/^[\s,]*(?:un|una)\s+kids\b/gim, 'kids'],
  [/\bpizza\s+de\s+grupo\b/gi, 'pizza grupo'],
  [/\bgrupo\s+para\s+dos\b/gi, 'pizza para dos'],
  [/\bpizza\s+para\s+dos\b/gi, 'pizza para dos'],
  [/\bpa\s+dos\b/gi, 'para dos'],
];

/** Aplica correcciones coloquiales al texto de pedido ( también antes de partir segmentos). */
export function normalizeOrderQueryPhrases(text) {
  let t = String(text || '');
  for (const [rx, rep] of ORDER_QUERY_PHRASE_FIXES) {
    t = t.replace(rx, rep);
  }
  return t.replace(/\s+/g, ' ').trim();
}

let catalogAliasCacheKey = '';
let catalogAliasIndex = null;

function catalogCacheKey(products) {
  return (products || [])
    .map((p) => `${p.id}:${p.name}:${p.sales_count ?? 0}`)
    .join('|');
}

/** Alias derivados de la carta: "big" → Bigmati, "50 piezas" → producto con 50 piezas, etc. */
export function buildCatalogOrderAliasIndex(products) {
  const key = catalogCacheKey(products);
  if (catalogAliasIndex && catalogAliasCacheKey === key) return catalogAliasIndex;

  const byAlias = new Map();
  const add = (alias, product, weight = 1) => {
    const k = normalizeAssistantText(alias);
    if (!k || k.length < 2) return;
    const list = byAlias.get(k) || [];
    list.push({ product, weight });
    byAlias.set(k, list);
  };

  for (const p of visibleMenuProducts(products)) {
    const name = normalizeAssistantText(p.name || '');
    if (!name) continue;

    if (/\bbigmati\b/.test(name)) {
      add('big', p, 4);
      add('bigmati', p, 3);
      if (/\bdoble\b/.test(name)) add('big doble', p, 3);
      if (/\bsimple\b/.test(name)) add('big simple', p, 3);
      if (/\bgrill\b/.test(name)) add('big grill', p, 3);
    }
    if (/\bclassic\b/.test(name) && /\bgrill\b/.test(name)) {
      add('classic', p, 3);
      add('classic grill', p, 3);
    }
    if (/\bkids\b/.test(name)) {
      if (/\b(duo|duo)\b/.test(name)) add('duo kids', p, 4);
      else add('kids', p, 4);
    }
    if (/\bsmash\b/.test(name)) add('smash', p, 3);

    const piezas = name.match(/(\d{1,3})\s*piezas?\b/);
    if (piezas) {
      const n = piezas[1];
      add(`${n} piezas`, p, 6);
      add(`${n} pieza`, p, 5);
    }

    if (/\bfamiliar\b/.test(name)) {
      add('familiar', p, 3);
      add('pizza familiar', p, 4);
    }
    if (/\bgrupo\b/.test(name)) {
      add('grupo', p, 3);
      add('pizza grupo', p, 4);
    }
    if (/\bpara\s+dos\b/.test(name)) {
      add('para dos', p, 3);
      add('pizza para dos', p, 4);
    }

    if (/\bpizza\b/.test(name)) {
      const flavor = name.replace(/\bpizza\b/g, '').replace(/\b(familiar|grupo|individual|\d+\s*piezas?)\b/g, '').trim();
      if (flavor.length >= 4) add(`pizza ${flavor}`.trim(), p, 2);
    }
  }

  for (const list of byAlias.values()) {
    list.sort(
      (a, b) =>
        b.weight - a.weight ||
        (Number(b.product.sales_count) || 0) - (Number(a.product.sales_count) || 0)
    );
  }

  catalogAliasCacheKey = key;
  catalogAliasIndex = byAlias;
  return byAlias;
}

function scoreQueryTokenOverlap(queryNorm, product) {
  const name = normalizeAssistantText(product?.name || '');
  if (!name || !queryNorm) return 0;
  if (name === queryNorm) return 100;
  if (queryNorm.includes(name) && name.length >= 4) return 90;

  const qTokens = queryNorm.split(/\s+/).filter((t) => t.length >= 2 && !/^\d+$/.test(t));
  if (!qTokens.length) return 0;

  let matched = 0;
  for (const t of qTokens) {
    if (name.includes(t)) matched += 1;
  }

  const nameTokens = name.split(/\s+/).filter((t) => t.length >= 3);
  const generic = new Set(['bigmati', 'big', 'pizza', 'extra', 'mati']);
  let conflicting = 0;
  for (const d of nameTokens) {
    if (generic.has(d)) continue;
    if (!queryNorm.includes(d)) conflicting += 1;
  }

  return matched * 10 - conflicting * 8;
}

/** Elige el producto del alias que mejor calza con la consulta completa (p. ej. "bigmati grill doble"). */
export function pickBestAliasHitForQuery(queryNorm, hits) {
  if (!hits?.length) return null;
  if (hits.length === 1) return hits[0];

  let best = hits[0];
  let bestScore = scoreQueryTokenOverlap(queryNorm, hits[0].product);
  for (let i = 1; i < hits.length; i += 1) {
    const s = scoreQueryTokenOverlap(queryNorm, hits[i].product);
    if (s > bestScore) {
      bestScore = s;
      best = hits[i];
    } else if (s === bestScore) {
      const cmp =
        hits[i].weight - best.weight ||
        (Number(hits[i].product?.sales_count) || 0) - (Number(best.product?.sales_count) || 0);
      if (cmp > 0) best = hits[i];
    }
  }
  return best;
}

/** Resuelve apodo exacto contra la carta ("big", "50 piezas", "pizza familiar"). */
export function resolveCatalogNicknameProduct(query, products) {
  const qn = normalizeAssistantText(query);
  if (!qn || qn.length < 2) return null;
  const index = buildCatalogOrderAliasIndex(products);
  const hits = index.get(qn);
  return hits?.[0]?.product || null;
}

/**
 * Variantes de consulta para matchear lenguaje coloquial + alias de la carta.
 * @returns {string[]}
 */
export function expandOrderProductQuery(rawQuery, products) {
  const raw = String(rawQuery || '').trim();
  if (!raw) return [];

  const q = normalizeOrderQueryPhrases(raw);
  const out = new Set([raw, q].filter((x) => x.length >= 2));
  const qn = normalizeAssistantText(q);
  const index = buildCatalogOrderAliasIndex(products);

  const nick = resolveCatalogNicknameProduct(q, products);
  if (nick?.name) out.add(String(nick.name));

  for (const [alias, hits] of index) {
    if (alias.length < 3) continue;
    if (qn === alias) {
      if (hits[0]?.product?.name) out.add(String(hits[0].product.name));
    } else if (qn.endsWith(` ${alias}`) || qn.startsWith(`${alias} `)) {
      const hit = pickBestAliasHitForQuery(qn, hits);
      if (hit?.product?.name) out.add(String(hit.product.name));
    }
  }

  const piezas = qn.match(/(\d{1,3})\s*piezas?\b/);
  if (piezas) {
    const hits = index.get(`${piezas[1]} piezas`) || index.get(`${piezas[1]} pieza`);
    if (hits?.[0]?.product?.name) out.add(hits[0].product.name);
    out.add(`${piezas[1]} piezas pizza`);
  }

  if (/\bfamiliar\b/.test(qn)) {
    const fam = index.get('pizza familiar') || index.get('familiar');
    if (fam?.[0]?.product?.name) out.add(fam[0].product.name);
  }
  if (/\bgrupo\b/.test(qn) || /\bpara\s+dos\b/.test(qn)) {
    const grp = index.get('pizza para dos') || index.get('pizza grupo') || index.get('grupo');
    if (grp?.[0]?.product?.name) out.add(grp[0].product.name);
  }

  return [...out].filter((x, i, a) => x.length >= 2 && a.indexOf(x) === i);
}

/** Bonus de score cuando la consulta menciona N piezas y el producto las incluye en el nombre. */
export function scorePiezasCountMatch(queryNorm, productNameNorm) {
  const m = String(queryNorm || '').match(/(\d{1,3})\s*piezas?\b/);
  if (!m) return 0;
  const num = m[1];
  if (productNameNorm.includes(`${num} piezas`) || productNameNorm.includes(`${num} pieza`)) {
    return 8;
  }
  if (productNameNorm.includes(num) && /\bpiezas?\b/.test(productNameNorm)) return 4;
  return 0;
}
